import React, {PureComponent, RefObject} from 'react'
import * as PropTypes from 'prop-types'
import {InferPropTypes} from '@modifi/typescript-utils'
import {times} from 'ramda'
import throttle from 'lodash.throttle'
import debounce from 'lodash.debounce'
import ms from 'ms'
import {SwipeEventData} from 'react-swipeable'
import {CardWrapper, CarouselContainer, Indicators} from './styles'
import {getElementWidth, sumAryValues} from './utils'
import {CarouselCardContext, CarouselContext} from './context'
import CarouselInner from './components/carousel-inner'

const propTypes = {
  indicatorPosition: PropTypes.oneOf<'left' | 'center' | 'right'>(['left', 'center', 'right']),
  cardAlignment: PropTypes.oneOf<'left' | 'center'>(['left', 'center']),
  children: PropTypes.node.isRequired,
  containerOverflow: PropTypes.bool,
}

const defaultProps = {
  indicatorPosition: 'center' as 'center',
  cardAlignment: 'center' as 'center',
  containerOverflow: false,
}

export type DefaultCarouselProps = InferPropTypes<typeof propTypes, typeof defaultProps>

type DefaultCarouselState = {
  activePageIndex: number

  containerWidth: number
  pageIndicatorCount: number
  scrollOffset: number | undefined
  isSwiping: boolean
  spaceOffset: number
}

export class DefaultCarousel extends PureComponent<DefaultCarouselProps, DefaultCarouselState> {
  static propTypes = propTypes

  static defaultProps = defaultProps

  protected containerRef: RefObject<HTMLDivElement>

  protected cardRefs: (HTMLElement | null)[] = []

  protected cardPages: number[] = []

  protected swipeStartOffset: number = 0
  protected swipeStartSlide: number = 0

  constructor(props: Readonly<DefaultCarouselProps>) {
    super(props)

    this.state = {
      activePageIndex: 0,
      pageIndicatorCount: 0,
      scrollOffset: undefined,
      isSwiping: false,
      containerWidth: 0,
      spaceOffset: 0,
    }

    this.containerRef = React.createRef<HTMLDivElement>()
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize, {passive: true})

    this.initializeSlides()
  }

  componentDidUpdate(
    prevProps: Readonly<DefaultCarouselProps>,
    prevState: Readonly<DefaultCarouselState>
  ) {
    const {scrollOffset, isSwiping, activePageIndex, containerWidth} = this.state
    if (isSwiping && prevState.scrollOffset !== scrollOffset) {
      const swipeStart = this.swipeStartOffset
      const swipeDistance = (scrollOffset ?? 0) - swipeStart

      const swipeDistancePercent = Math.abs(swipeDistance) / containerWidth

      const isPageChange = swipeDistancePercent > 0.2 && Math.abs(swipeDistance) > 100
      const pageAdditions = Math.floor(swipeDistancePercent)

      // add page index
      if (isPageChange) {
        if (swipeDistance > 0) {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({activePageIndex: this.swipeStartSlide + 1 + pageAdditions})
        } else {
          // eslint-disable-next-line react/no-did-update-set-state
          this.setState({activePageIndex: this.swipeStartSlide - 1 - pageAdditions})
        }
      }
    }
    if (isSwiping === false && prevState.isSwiping === true) {
      this.updateActivePage(activePageIndex)
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize = () => {
    this.initializeSlides()
  }

  handleScrollOffsetChange = (
    newOffset: number | ((oldOffset: number | undefined) => number | undefined)
  ) => {
    const {containerWidth, spaceOffset} = this.state

    const limiter = (newOffsetValue: number | undefined): number | undefined => {
      if (typeof newOffsetValue === 'undefined' || newOffsetValue <= spaceOffset) {
        return spaceOffset
      }
      const cardWidths = this.getCardWidths()

      const maxOffset = sumAryValues(cardWidths) - containerWidth
      return Math.min(newOffsetValue, maxOffset)
    }

    if (typeof newOffset === 'function') {
      this.setState(({scrollOffset}) => ({
        scrollOffset: limiter(newOffset(scrollOffset)),
      }))
    } else {
      this.setState({scrollOffset: limiter(newOffset)})
    }
  }

  handleSwiping = throttle(
    (eventData: SwipeEventData) => {
      const {isSwiping, spaceOffset} = this.state

      // using space offset to find out if we actually should scroll
      // if spaceOffset is unequal to 0 in case the sum of card widths is smaller than the container width
      if (
        (spaceOffset === 0 && isSwiping && eventData.dir === 'Left') ||
        eventData.dir === 'Right'
      ) {
        this.handleScrollOffsetChange(this.swipeStartOffset - eventData.deltaX)
      }
    },
    ms('50ms'),
    {trailing: false}
  )

  getCardWidths = (): number[] => this.cardRefs.map(el => getElementWidth(el))

  initializeSlides = () => {
    const {cardAlignment} = this.props
    const {activePageIndex} = this.state

    const containerWidth = getElementWidth(this.containerRef?.current)

    if (containerWidth) {
      let currentPage = 0
      let pageSpaceLeft = containerWidth

      // lets keep the first card of the currently active page
      const firstCardOnPage = this.getCardIndexesOnPage(activePageIndex)[0]

      this.cardRefs.forEach((el, index) => {
        if (el === null) {
          this.cardPages[index] = -1
          return
        }

        const elWidth = getElementWidth(el)

        // the current card does not fit anymore into the current page
        if (elWidth > pageSpaceLeft) {
          // start a new page
          if (pageSpaceLeft !== containerWidth || index !== 0) {
            // condition to protect the first page to be empty
            currentPage += 1
          }
          pageSpaceLeft = containerWidth - elWidth
        } else {
          // the current card is part of the previous page
          pageSpaceLeft -= elWidth
        }
        this.cardPages[index] = currentPage
      })

      let spaceOffset = 0
      if (currentPage === 0 && pageSpaceLeft > 0 && cardAlignment === 'center') {
        // in case we only show one page, there is still space left and card should be aligned center
        // we store half of the pageSpaceLeft in store to apply it to the offset calculation later
        spaceOffset = (pageSpaceLeft / 2) * -1
      }

      this.setState(
        {
          pageIndicatorCount: currentPage + 1,
          containerWidth,
          spaceOffset,
        },
        () => {
          // if the card had a page assigned lets check if the first card is on a new page now
          const maybeUpdatedPageOfCard = this.cardPages[firstCardOnPage ?? 0]

          if (maybeUpdatedPageOfCard !== activePageIndex) {
            // card moved to different page - lets scroll to this page
            this.updateActivePage(maybeUpdatedPageOfCard)
          } else {
            this.updateActivePage(activePageIndex)
          }
        }
      )
    }
  }

  // eslint-disable-next-line react/sort-comp
  debouncedInitializeSlides = debounce(this.initializeSlides, ms('50ms'))

  updateActivePage = (pageIndex: number) => {
    const activeCardIndex = this.getFirstCardIndexOnPage(pageIndex)
    this.scrollToCardIndex(activeCardIndex)
    this.setState({
      activePageIndex: pageIndex,
    })
  }

  getCardIndexesOnPage = (pageIndex: number): number[] =>
    this.cardPages.reduce((res, p, index) => {
      if (p === pageIndex) {
        res.push(index)
      }
      return res
    }, [] as number[])

  getFirstCardIndexOnPage = (pageIndex: number): number =>
    this.cardPages.findIndex(p => p === pageIndex)

  handleSwiped = () => {
    this.setState({isSwiping: false})
  }

  handleSwipeStart = () => {
    const {activePageIndex} = this.state
    const {scrollOffset} = this.state
    this.swipeStartOffset = scrollOffset ?? 0
    this.swipeStartSlide = activePageIndex
    this.setState({isSwiping: true})
  }

  scrollToCardIndex = (cardIndex: number): void => {
    const {cardAlignment} = this.props
    const {containerWidth} = this.state

    const pageIndex = this.cardPages[cardIndex]
    if (pageIndex === -1) {
      // eslint-disable-next-line no-console
      console.error(`ERR 1`)
      return
    }
    const cardWidths = this.getCardWidths()

    const pageWidths = this.getCardIndexesOnPage(pageIndex).reduce(
      (res, i) => res + cardWidths[i],
      0
    )
    const baseOffset = sumAryValues(cardWidths.slice(0, cardIndex))

    let alignmentAdjustment = 0
    if (cardAlignment === 'center') {
      alignmentAdjustment = (containerWidth - pageWidths) / 2
    }

    this.handleScrollOffsetChange(baseOffset - alignmentAdjustment)
  }

  setCardRef = (el: HTMLElement | null, index: number) => {
    const {containerWidth} = this.state

    this.cardRefs[index] = el

    if (getElementWidth(this.containerRef.current) !== containerWidth) {
      this.debouncedInitializeSlides()
    }
  }

  renderPageSwitch(): React.ReactNode {
    const {indicatorPosition} = this.props
    const {pageIndicatorCount, activePageIndex} = this.state

    if (pageIndicatorCount <= 1) {
      return null
    }
    return (
      <Indicators position={indicatorPosition!}>
        {times(
          pageIndex => (
            <button
              data-isactive={pageIndex === activePageIndex}
              type="button"
              onClick={() => this.updateActivePage(pageIndex)}
              // eslint-disable-next-line react/no-array-index-key
              key={`carouselIndicator-${pageIndex}`}
              aria-label="scroll buttons"
            />
          ),
          pageIndicatorCount
        )}
      </Indicators>
    )
  }

  renderChildren = () => {
    const {children} = this.props
    const {activePageIndex, isSwiping} = this.state

    return React.Children.map(children, (child, index) => {
      const cardPageIndex = this.cardPages[index]
      const cardsOnPage = this.getCardIndexesOnPage(cardPageIndex)

      // while swiping we are removing the onclick event to prevent any glitches in combination with
      // this onclick and the "stop swiping" behavior
      const handleClick = () => {
        if (!isSwiping) {
          const page = this.cardPages[index]
          this.updateActivePage(page)
        }
      }
      return (
        <CarouselCardContext.Provider
          value={{
            cardIndex: index,
            cardIndexOnSlide: cardsOnPage?.indexOf(index) ?? 0,
            cardsOnSlide: cardsOnPage?.length ?? 0,
            cardIsVisible: activePageIndex === cardPageIndex,
          }}
        >
          <CardWrapper
            data-card-index={index}
            ref={(el: HTMLElement | null) => {
              this.setCardRef(el, index)
            }}
            // use onMouseUp to quicker get the click through
            // onClick was triggered always after isSwiping was false already
            onMouseUp={handleClick}
            // eslint-disable-next-line react/no-array-index-key
            key={index}
          >
            {child}
          </CardWrapper>
        </CarouselCardContext.Provider>
      )
    })
  }

  render() {
    const {children, containerOverflow} = this.props
    const {scrollOffset, isSwiping, activePageIndex, containerWidth, spaceOffset} = this.state

    return (
      <CarouselContext.Provider
        value={{
          isSwiping,
          currentSlideIndex: activePageIndex,
          containerWidth,
          maxCardIndex: React.Children.count(children) - 1,
          notifyUpdatedCards: this.debouncedInitializeSlides,
        }}
      >
        <CarouselContainer
          ref={this.containerRef}
          onSwipeStart={this.handleSwipeStart}
          onSwiping={this.handleSwiping}
          onSwiped={this.handleSwiped}
          containerOverflow={containerOverflow}
        >
          <CarouselInner
            scrollOffset={scrollOffset}
            isSwiping={isSwiping}
            swipeable={spaceOffset === 0}
          >
            {this.renderChildren()}
          </CarouselInner>
        </CarouselContainer>
        {this.renderPageSwitch()}
      </CarouselContext.Provider>
    )
  }
}

export default DefaultCarousel
